import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

/**
 * Reads the current user's Keycloak roles from the JWT and exposes them
 * synchronously and reactively.
 *
 * <h2>Token discovery</h2>
 *
 * <p>ACA stores the Keycloak token in localStorage under the key managed by
 * the Alfresco JS API. We use the standard discovery path:
 * <ol>
 *   <li>Look for the token in localStorage under common keys
 *       (`access_token`, `oauth2_user_token`).</li>
 *   <li>If not found, parse the {@code Authorization} cookie if the auth
 *       layer set one.</li>
 *   <li>Fall back to empty roles — guards will deny admin access.</li>
 * </ol>
 *
 * <p>If your ACA build uses a different token storage scheme, override the
 * {@link #readToken} method or inject your own implementation. The rest of
 * the admin module talks to this service via {@link #hasAnyRole}, so a
 * single override re-routes everything.
 *
 * <h2>Role conventions used by the admin module</h2>
 *
 * <ul>
 *   <li>{@code wf-admin} — full admin access (sees every page)</li>
 *   <li>{@code wf-bpmn-editor} — BPMN list + upload + visual editor</li>
 *   <li>{@code wf-form-designer} — form designer pages</li>
 *   <li>{@code wf-binding-editor} — step bindings</li>
 *   <li>{@code wf-rule-editor} — notification rules CRUD</li>
 *   <li>{@code wf-template-editor} — notification template CRUD</li>
 * </ul>
 *
 * <p>{@code wf-admin} is treated as a wildcard — any check for any of the
 * above roles passes if the user has {@code wf-admin}.
 *
 * <h2>Reactive variant</h2>
 *
 * <p>{@link #roles$} is a BehaviorSubject so menu / sidebar templates can
 * bind via {@code | async} and re-render automatically if the token is
 * refreshed.
 */
@Injectable({ providedIn: 'root' })
export class WfPermissionService {

  static readonly ROLE_ADMIN = 'wf-admin';
  /** Realm-wide ADMIN role — also acts as a wildcard for every wf-* check. */
  static readonly ROLE_REALM_ADMIN = 'ADMIN';
  static readonly ROLE_BPMN_EDITOR = 'wf-bpmn-editor';
  static readonly ROLE_FORM_DESIGNER = 'wf-form-designer';
  static readonly ROLE_BINDING_EDITOR = 'wf-binding-editor';
  static readonly ROLE_RULE_EDITOR = 'wf-rule-editor';
  static readonly ROLE_TEMPLATE_EDITOR = 'wf-template-editor';

  private readonly _roles$ = new BehaviorSubject<Set<string>>(new Set());
  readonly roles$: Observable<Set<string>> = this._roles$.asObservable();

  constructor() {
    this.refresh();
  }

  /** Re-read the JWT and republish the role set. Call after token refresh. */
  refresh(): void {
    this._roles$.next(this.parseRolesFromToken());
  }

  /** Synchronous check — returns true if the user has the role, ADMIN, or wf-admin. */
  hasRole(role: string): boolean {
    const roles = this._roles$.value;
    return this.isWildcardRoleHolder(roles) || roles.has(role);
  }

  /** True if the user has any of the listed roles, ADMIN, or wf-admin. */
  hasAnyRole(roles: string[]): boolean {
    if (!roles || roles.length === 0) return true; // no requirement → allow
    const userRoles = this._roles$.value;
    if (this.isWildcardRoleHolder(userRoles)) return true;
    return roles.some(r => userRoles.has(r));
  }

  /** True only if the user has wf-admin or ADMIN specifically. */
  isAdmin(): boolean {
    return this.isWildcardRoleHolder(this._roles$.value);
  }

  /**
   * ADMIN (realm-wide) and wf-admin (workflow-specific) both grant universal
   * access to every admin page. Case-insensitive on ADMIN to match
   * MenuService backend's role-comparison logic.
   */
  private isWildcardRoleHolder(roles: Set<string>): boolean {
    if (roles.has(WfPermissionService.ROLE_ADMIN)) return true;
    for (const r of roles) {
      if (r === WfPermissionService.ROLE_REALM_ADMIN) return true;
      if (r.toUpperCase() === 'ADMIN' || r.toUpperCase() === 'ROLE_ADMIN') return true;
      if (r.toUpperCase() === 'SKYPLUS_ADMIN') return true;
    }
    return false;
  }

  /** Reactive version of {@link #hasAnyRole}, for use in templates. */
  hasAnyRole$(roles: string[]): Observable<boolean> {
    return this.roles$.pipe(map(() => this.hasAnyRole(roles)));
  }

  // ── Token plumbing ───────────────────────────────────────────────────────

  private parseRolesFromToken(): Set<string> {
    const token = this.readToken();
    if (!token) return new Set();
    try {
      const payload: any = this.decodeJwtPayload(token);
      const realm: string[] = payload?.realm_access?.roles ?? [];
      const resourceClient: string[] = payload?.resource_access?.['skyplus']?.roles ?? [];
      return new Set<string>([...realm, ...resourceClient]);
    } catch (e) {
      console.warn('WfPermissionService: failed to decode JWT', e);
      return new Set();
    }
  }

  /**
   * Override this in your ACA app if you store the token differently. The
   * default checks the common ACA / Keycloak storage locations.
   */
  protected readToken(): string | null {
    // ACA stores it via Alfresco JS API as ALFRESCO_AUTH_DATA, or
    // Keycloak adapters often use access_token directly.
    const candidates = [
      'access_token',
      'kc_token',
      'oauth2_user_token',
      'ALFRESCO_AUTH_DATA',
    ];
    for (const key of candidates) {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      // Some keys store JSON blobs; look for a token-shaped value inside
      if (raw.startsWith('eyJ')) return raw; // bare JWT
      try {
        const parsed = JSON.parse(raw);
        if (typeof parsed === 'string' && parsed.startsWith('eyJ')) return parsed;
        if (parsed?.token?.startsWith('eyJ')) return parsed.token;
        if (parsed?.access_token?.startsWith('eyJ')) return parsed.access_token;
      } catch {
        // not JSON, skip
      }
    }
    return null;
  }

  private decodeJwtPayload(token: string): Record<string, unknown> {
    const parts = token.split('.');
    if (parts.length !== 3) throw new Error('Not a JWT');
    const payload = parts[1];
    // base64url → base64
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const json = atob(padded);
    return JSON.parse(json);
  }
}
